import { useMemo } from 'react';
import { useAllProducts } from './useAllProducts';
import { findBundleForSelection } from '@/services/shopify';
import { findBundleMapping } from '@/utils/bundleMappings';
import type { ShopifyBundle } from '@/types/shopify';
import type { Equipo, Mascarilla } from '@/context/AppContext';
import type { Kit } from '@/types/kit';

export interface BundleSelectionResult {
  bundle: ShopifyBundle | null;
  fallbackBundle: {
    id: string;
    name: string;
    description: string;
    price: number;
    image: string;
    components: Array<{
      name: string;
      type: string;
    }>;
  } | null;
  isLoading: boolean;
  error: Error | null;
  // Pricing cache data
  cachedPrice?: number;
  priceTimestamp?: Date;
  pricingSource: 'shopify' | 'fallback' | 'cache';
}

// Overloaded hook to support both individual products and kits
export function useBundleSelection(
  equipoSeleccionado: Equipo | null,
  mascarillaSeleccionada: Mascarilla | null
): BundleSelectionResult;
export function useBundleSelection(
  kit: Kit | null
): BundleSelectionResult;
export function useBundleSelection(
  equipoOrKit: Equipo | Kit | null,
  mascarillaSeleccionada?: Mascarilla | null
): BundleSelectionResult {
  const { bundles, isLoading, error } = useAllProducts();

  const result = useMemo(() => {
    // Check if we're dealing with a kit or individual products
    const isKit = equipoOrKit && 'components' in equipoOrKit;
    
    let equipoSeleccionado: Equipo | null = null;
    let mascarillaSeleccionadaLocal: Mascarilla | null = null;
    
    if (isKit) {
      const kit = equipoOrKit as Kit;
      // Extract equipo and mascarilla from kit components
      const equipoComponent = kit.components.find(c => c.type === 'equipo');
      const mascarillaComponent = kit.components.find(c => c.type === 'mascarilla');
      
      if (equipoComponent) {
        equipoSeleccionado = {
          marca: equipoComponent.marca,
          modelo: equipoComponent.modelo,
          precio: 0, // Pricing now comes from Shopify
          conectividad: equipoComponent.conectividad || 'Bluetooth',
          garantia: equipoComponent.garantia || '1 año',
          caracteristicas: equipoComponent.caracteristicas,
          imagen: equipoComponent.imagen
        };
      }
      
      if (mascarillaComponent) {
        mascarillaSeleccionadaLocal = {
          marca: mascarillaComponent.marca,
          modelo: mascarillaComponent.modelo,
          precio: 0, // Pricing now comes from Shopify
          tipo: mascarillaComponent.tipo || 'Nasal',
          caracteristicas: mascarillaComponent.caracteristicas,
          compatibilidad: mascarillaComponent.compatibilidad || [],
          imagen: mascarillaComponent.imagen
        };
      }
    } else {
      equipoSeleccionado = equipoOrKit as Equipo | null;
      mascarillaSeleccionadaLocal = mascarillaSeleccionada || null;
    }
    
    if (!equipoSeleccionado || !mascarillaSeleccionadaLocal) {
      return {
        bundle: null,
        fallbackBundle: null,
        isLoading,
        error,
        pricingSource: 'fallback' as const
      };
    }

    // ALWAYS use hardcoded bundle mappings - never pattern matching
    const bundleMapping = findBundleMapping(equipoSeleccionado, mascarillaSeleccionadaLocal);
    console.log('[BUNDLE] Found mapping:', bundleMapping?.shopifyHandle);
    console.log('[BUNDLE] Available bundles:', bundles.length, bundles.map(b => b.handle));
    
    let shopifyBundle: ShopifyBundle | null = null;
    if (bundleMapping) {
      // First try to find in loaded bundles
      shopifyBundle = bundles.find(bundle => bundle.handle === bundleMapping.shopifyHandle) || null;
      console.log('[BUNDLE] Found Shopify bundle:', !!shopifyBundle, shopifyBundle?.price);
      
      // If not found in loaded bundles, this means Shopify API didn't return the bundle
      if (!shopifyBundle) {
        console.error(`[BUNDLE] Shopify API missing bundle: ${bundleMapping.shopifyHandle}`);
        console.error(`[BUNDLE] Available handles:`, bundles.map(b => b.handle));
        return {
          bundle: null,
          fallbackBundle: null,
          isLoading,
          error: new Error(`Shopify bundle not found: ${bundleMapping.shopifyHandle}`),
          pricingSource: 'fallback' as const
        };
      }
    }
    
    // If we have a shopifyBundle at this point, return it with real Shopify data
    if (shopifyBundle) {
      console.log('[BUNDLE] Returning real Shopify bundle with price:', shopifyBundle.price);
      return {
        bundle: shopifyBundle,
        fallbackBundle: null, // No fallback needed when we have real data
        isLoading,
        error,
        cachedPrice: shopifyBundle.price,
        priceTimestamp: new Date(),
        pricingSource: 'shopify' as const
      };
    }
    
    // If we reach here, something is wrong - we should have found a bundle
    console.error('[BUNDLE] No bundle found despite having mapping:', bundleMapping?.shopifyHandle);
    return {
      bundle: null,
      fallbackBundle: null,
      isLoading,
      error: new Error('Bundle lookup failed'),
      pricingSource: 'fallback' as const
    };
  }, [equipoOrKit, mascarillaSeleccionada, bundles, isLoading, error]);

  return result;
};

// Helper function to get bundle info for cart/checkout
interface FallbackBundle {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  components: Array<{
    name: string;
    type: string;
  }>;
}

export const getBundleInfo = (
  equipoSeleccionado: Equipo | null,
  mascarillaSeleccionada: Mascarilla | null,
  shopifyBundle: ShopifyBundle | null,
  fallbackBundle: FallbackBundle | null,
  maskSizing?: { maskSize?: string }
) => {
  if (!equipoSeleccionado || !mascarillaSeleccionada) return null;

  // Get bundle mapping for additional data
  const bundleMapping = findBundleMapping(equipoSeleccionado, mascarillaSeleccionada);

  // If we have a Shopify bundle, use its pricing and details
  if (shopifyBundle) {
    // Try to get components from fallback bundle first, fallback to creating from individual products
    let processedComponents: Array<{ name: string; type: string }> = [];
    
    if (fallbackBundle?.components) {
      processedComponents = fallbackBundle.components.map((comp) => {
        const isMask = comp.type.includes('Mascarilla');
        const maskSize = maskSizing?.maskSize;
        
        if (isMask && maskSize) {
          const sizeLabel = {'S': 'Pequeña', 'M': 'Mediana', 'L': 'Grande'}[maskSize];
          return {
            ...comp,
            type: `${comp.type}<br/><b>Talla:</b> ${sizeLabel}`
          };
        }
        
        return comp;
      });
    } else {
      // Create components from individual products when no fallback bundle available
      processedComponents = [
        {
          name: `${equipoSeleccionado.marca} ${equipoSeleccionado.modelo}`,
          type: 'Equipo CPAP Automático'
        },
        {
          name: `${mascarillaSeleccionada.marca} ${mascarillaSeleccionada.modelo}`,
          type: maskSizing?.maskSize 
            ? `Mascarilla ${mascarillaSeleccionada.tipo}<br/><b>Talla:</b> ${{'S': 'Pequeña', 'M': 'Mediana', 'L': 'Grande'}[maskSizing.maskSize]}`
            : `Mascarilla ${mascarillaSeleccionada.tipo}`
        }
      ];
      
      console.log('🔧 Created components from individual products (no fallback bundle):', processedComponents);
    }
    
    return {
      id: shopifyBundle.handle, // Use handle for cart
      shopifyId: shopifyBundle.id,
      name: shopifyBundle.title,
      description: shopifyBundle.description,
      price: shopifyBundle.price,
      image: shopifyBundle.images[0]?.url || fallbackBundle?.image || '',
      isShopifyBundle: true,
      variants: shopifyBundle.variants,
      bundleMapping, // Add mapping data for cart generation
      components: processedComponents
    };
  }

  // Otherwise use fallback bundle - but don't use calculated pricing if we have a mapping
  if (!fallbackBundle) return null;
  
  console.warn(`No Shopify bundle found for ${equipoSeleccionado.marca} + ${mascarillaSeleccionada.modelo}, using fallback pricing`);
  
  const processedFallbackComponents = fallbackBundle.components.map((comp) => {
    const isMask = comp.type.includes('Mascarilla');
    const maskSize = maskSizing?.maskSize;
    
    if (isMask && maskSize) {
      const sizeLabel = {'S': 'Pequeña', 'M': 'Mediana', 'L': 'Grande'}[maskSize];
      return {
        ...comp,
        type: `${comp.type}<br/><b>Talla:</b> ${sizeLabel}`
      };
    }
    
    return comp;
  });
  
  return {
    ...fallbackBundle,
    isShopifyBundle: false,
    variants: [],
    bundleMapping, // Add mapping data even for fallback
    components: processedFallbackComponents
  };
};